import React, { CSSProperties, useEffect, useState } from 'react';
import mustache from 'mustache';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
  NarvarClickTypes,
  NarvarClickSubTypes,
} from '../../../../../../hooks/Analytics';
import { NotificationPreferences } from '../../../../../../../../__generated__/loma';
import { useSettings } from '../../../../../../hooks/useSettings';
import TelephoneInput from '../../TelephoneInput/TelephoneInput';
import Checkbox from '../../../../../ui/Checkbox/Checkbox';
import Button, { ButtonType } from '../../../../../ui/Button/BlockButton';
import { useTranslations } from '../../../../../../hooks/useTranslations';
import { useTheme } from '../../../../../../hooks/useTheme';
import { LayoutTree } from '../../../../DynamicLayout';
import MustacheNestedRender from '../../../../../ui/MustacheNestedRender';
import { PreferenceLevels } from '../../../types';
import NotificationPreferencesOptin from './NotificationPreferencesOptin';
import { ERROR_TYPES } from '../../..';
import { templateLinkContext } from '../../common/TemplateLinkContext';
import { useConsumerPreferences } from '../../../hooks/useConsumerPreferences';
import { useGlobalSettings } from '../../../../../../hooks/useGlobalSettings';
import styles from './SMSOptinForm.module.css';

const { FeatureLayout } = LayoutTree;

export interface SMSOptInProps {
  onOptIn: (
    contactInfo: string,
    retailerLevel: boolean,
    savePreferencesChecked?: boolean,
    marketingOptInChecked?: boolean,
  ) => void;
  onChange: () => void;
  featureName?: string;
  validationError?: ERROR_TYPES;
  layout?: LayoutTree.FeatureLayout;
  notificationPreferencesState?: NotificationPreferences;
  consumerPreferencesOptinEnabled?: boolean;
  retailerMarketingOptinEnabled?: boolean;
}

export const SMSOptIn = ({
  onOptIn,
  onChange,
  featureName,
  validationError,
  layout,
  notificationPreferencesState,
  consumerPreferencesOptinEnabled = false,
  retailerMarketingOptinEnabled,
}: SMSOptInProps) => {
  const { preferenceLevel } = notificationPreferencesState ?? {};

  const { optinEnabled } = useConsumerPreferences();

  const [retailerLevelChecked, setRetailerLevelChecked] = useState(
    preferenceLevel === PreferenceLevels.RETAILER,
  );
  const [marketingOptInChecked, setMarketingOptInChecked] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');

  const [notificationPreferencesChecked, setnotificationPreferencesChecked] =
    useState(consumerPreferencesOptinEnabled);

  useEffect(() => {
    if (!consumerPreferencesOptinEnabled) return;
    setnotificationPreferencesChecked(consumerPreferencesOptinEnabled);
  }, [consumerPreferencesOptinEnabled]);

  const { notificationCenter } = useSettings();
  const { marketingOptInPrivacyLink = '', marketingOptInTermsLink = '' } =
    notificationCenter ?? {};

  const { retailerLevelOptInEnabled, retailerName } = useGlobalSettings();

  const {
    bodytextDefaultNone: bodyTextStyle,
    captiontextErrorNone: errorTextStyle,
    linksNoneDefault: linkStyle,
    linksNoneHover: linkeHoverStyle,
  } = useTheme();

  const {
    notificationCenterMarketingOptInText,
    notificationCenterSmsDisclaimerText,
    notificationCenterSmsDisclaimerTextMarketing,
    notificationCenterSmsInputLabel,
    notificationCenterSmsSignupButton,
    notificationCenterSmsValidationErrorMessage,
    notificationCenterRetailerLevelSubscribeOption,
    notificationCenterSublimeErrorMessage,
  } = useTranslations();

  const logEvent = useAnalyticsPost();

  const isLandscape = layout === FeatureLayout.LANDSCAPE;

  const marketingOptInAnalytics = {
    clickNotificationMarketingOptInChecked: marketingOptInChecked,
    clickNotificationMarketingPrivacyLink: marketingOptInPrivacyLink,
    clickNotificationMarketingTermsLink: marketingOptInTermsLink,
    clickNotificationMarketingOptInText: mustache.render(
      notificationCenterMarketingOptInText || '',
      { retailerName, privacyLink: true, termsLink: true },
    ),
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const clickNotificationOptInDisclaimerText = mustache.render(
      (retailerMarketingOptinEnabled
        ? notificationCenterSmsDisclaimerTextMarketing
        : notificationCenterSmsDisclaimerText) ?? '',
      { retailerName },
    );
    logEvent({
      clickCategory: NarvarClickSubTypes.SMS_SIGNUP,
      clickNotificationIsOptIn: true,
      clickSubType: NarvarClickSubTypes.SMS_SIGNUP,
      clickType: NarvarClickTypes.INTERACTION,
      eventComponent: NarvarEventComponents.NTH_PACK_NOTIFICATION_CENTER,
      eventFeature: NarvarEventComponents.NTH_PACK_NOTIFICATION_CENTER,
      eventType: NarvarEventTypes.CLICK,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_PACK_NOTIFICATION_CENTER,
      clickNotificationOptInDisclaimerText,
      ...(retailerMarketingOptinEnabled ? marketingOptInAnalytics : {}),
    });
    onOptIn(
      phoneNumber,
      retailerLevelChecked,
      marketingOptInChecked,
      notificationPreferencesChecked,
    );
  };

  const handleNotificationPreferencesChange = () => {
    setnotificationPreferencesChecked((prevValue) => !prevValue);
    onChange();
  };

  const handlePhoneNumberChange = (number: string) => {
    setPhoneNumber(number);

    // This is a hack because TelephoneInput useEffect's dependency arrays are
    // not exhaustive and are firing change events on load
    if (number.length > 0) onChange();
  };

  const showMarketingOptinCheckbox =
    retailerMarketingOptinEnabled && !consumerPreferencesOptinEnabled;

  const showRetailerLevelOptinCheckbox =
    retailerLevelOptInEnabled &&
    (preferenceLevel === PreferenceLevels.RETAILER ||
      preferenceLevel === PreferenceLevels.ORDER);

  return (
    <form noValidate onSubmit={handleSubmit}>
      <div className={styles.smsOptinFormContainer}>
        <div
          data-testid={`column-one-${isLandscape ? '7' : '12'}`}
          style={{ gridColumn: `span ${isLandscape ? '7' : '12'}` }}
        >
          <TelephoneInput
            label={notificationCenterSmsInputLabel ?? ''}
            title={notificationCenterSmsInputLabel ?? ''}
            centered={!isLandscape}
            error={validationError !== undefined}
            onChangeCallback={handlePhoneNumberChange}
            featureName={featureName}
            s={
              validationError
                ? {
                    border: `1px solid ${errorTextStyle?.color ?? ''}`,
                  }
                : undefined
            }
          />
          {validationError && (
            <div
              data-test="smsOptInValidationErrorContainer"
              style={{ ...bodyTextStyle, ...errorTextStyle }}
            >
              {validationError === ERROR_TYPES.SUBLIME_VALIDATION_ERROR
                ? notificationCenterSublimeErrorMessage
                : notificationCenterSmsValidationErrorMessage}
            </div>
          )}
          {showMarketingOptinCheckbox && (
            <Checkbox
              id="marketingOptIn"
              onChange={(): void =>
                setMarketingOptInChecked(!marketingOptInChecked)
              }
              checked={marketingOptInChecked}
              containerStyle={{ marginTop: '1rem' }}
              labelTextCellStyle={{ textAlign: 'left' }}
            >
              <MustacheNestedRender
                template={mustache.parse(
                  notificationCenterMarketingOptInText ?? '',
                )}
                context={
                  new mustache.Context({
                    retailerName,
                    privacyLink: () =>
                      templateLinkContext(
                        marketingOptInPrivacyLink ?? '',
                        linkStyle,
                        linkeHoverStyle,
                      ),
                    termsLink: () =>
                      templateLinkContext(
                        marketingOptInTermsLink ?? '',
                        linkStyle,
                        linkeHoverStyle,
                      ),
                  })
                }
              />
            </Checkbox>
          )}
          {showRetailerLevelOptinCheckbox && (
            <Checkbox
              id="retailerLevel"
              onChange={(): void =>
                setRetailerLevelChecked(!retailerLevelChecked)
              }
              checked={retailerLevelChecked}
              containerStyle={{ marginTop: '1rem' }}
              labelTextCellStyle={{ textAlign: 'left' }}
            >
              {mustache.render(
                notificationCenterRetailerLevelSubscribeOption ?? '',
                { retailerName },
              )}
            </Checkbox>
          )}
        </div>
        {!isLandscape && optinEnabled && (
          <NotificationPreferencesOptin
            isLandscape={isLandscape}
            isChecked={notificationPreferencesChecked}
            onChange={handleNotificationPreferencesChange}
          />
        )}
        <div
          data-testid={`column-two-${isLandscape ? '5' : '12'}`}
          style={{ gridColumn: `span ${isLandscape ? '5' : '12'}` }}
        >
          <Button big type={ButtonType.submit}>
            {notificationCenterSmsSignupButton}
          </Button>
        </div>
        {isLandscape && optinEnabled && (
          <NotificationPreferencesOptin
            isLandscape={isLandscape}
            isChecked={notificationPreferencesChecked}
            onChange={handleNotificationPreferencesChange}
          />
        )}
      </div>
    </form>
  );
};

export default SMSOptIn;
